import React, { useState, useEffect } from 'react';
import {
  Mail, Send, Users, Filter, Search, Plus, X,
  BarChart3, Clock, Check, Eye, Trash2, Copy, Edit,
  FileText, Sparkles
} from 'lucide-react';
import './EmailCampaigns.css';

const EmailCampaigns = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Campaign form state
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    type: 'EMAIL',
    audience: 'ALL_USERS',
    merchantId: '',
    templateId: '',
    subject: '',
    content: '',
    scheduledFor: '',
    tags: []
  });

  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    content: '',
    description: '',
    category: 'GENERAL'
  });

  // Form validation
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (showCreateModal) {
      fetchMerchants();
    }
  }, [showCreateModal]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/email-campaigns', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns || []);
      }
    } catch (err) {
      console.error('Fetch campaigns error:', err);
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch('/api/admin/email-templates', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Fetch templates error:', err);
    }
  };

  const fetchMerchants = async () => {
    try {
      const response = await fetch('/api/admin/merchants', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setMerchants(data.merchants || []);
      }
    } catch (err) {
      console.error('Fetch merchants error:', err);
    }
  };

  const validateCampaignForm = () => {
    const errors = {};

    if (!campaignForm.name.trim()) {
      errors.name = 'Campaign name is required';
    }

    if (!campaignForm.subject.trim()) {
      errors.subject = 'Subject is required';
    }

    if (!campaignForm.content.trim()) {
      errors.content = 'Content is required';
    }

    if (campaignForm.audience === 'MERCHANT_CUSTOMERS' && !campaignForm.merchantId) {
      errors.merchantId = 'Please select a merchant';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateTemplateForm = () => {
    const errors = {};

    if (!templateForm.name.trim()) {
      errors.name = 'Template name is required';
    }

    if (!templateForm.subject.trim()) {
      errors.subject = 'Subject is required';
    }

    if (!templateForm.content.trim()) {
      errors.content = 'Content is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCampaign = async () => {
    if (!validateCampaignForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await fetch('/api/admin/email-campaigns/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(campaignForm)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Campaign created successfully!');
        setShowCreateModal(false);
        resetCampaignForm();
        fetchCampaigns();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to create campaign');
      }
    } catch (err) {
      console.error('Create campaign error:', err);
      setError('Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCampaign = async () => {
    if (!validateCampaignForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await fetch('/api/admin/email-campaigns/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(campaignForm)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Campaign updated successfully!');
        setShowCreateModal(false);
        setSelectedCampaign(null);
        resetCampaignForm();
        fetchCampaigns();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to update campaign');
      }
    } catch (err) {
      console.error('Update campaign error:', err);
      setError('Failed to update campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!validateTemplateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const url = selectedTemplate
        ? `/api/admin/email-templates/${selectedTemplate.id}`
        : '/api/admin/email-templates';

      const method = selectedTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(templateForm)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(selectedTemplate ? 'Template updated successfully!' : 'Template created successfully!');
        setShowTemplateModal(false);
        setSelectedTemplate(null);
        resetTemplateForm();
        fetchTemplates();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to save template');
      }
    } catch (err) {
      console.error('Save template error:', err);
      setError('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/admin/email-templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Template deleted successfully!');
        fetchTemplates();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to delete template');
      }
    } catch (err) {
      console.error('Delete template error:', err);
      setError('Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCampaign = async (campaign) => {
    setSelectedCampaign(campaign);
    setShowViewModal(true);
  };

  const handleEditCampaign = (campaign) => {
    setCampaignForm({
      name: campaign.name,
      type: campaign.type,
      audience: campaign.audience,
      merchantId: campaign.merchantId || '',
      templateId: campaign.templateId || '',
      subject: campaign.subject,
      content: campaign.content,
      scheduledFor: campaign.scheduledFor ? new Date(campaign.scheduledFor).toISOString().slice(0, 16) : '',
      tags: campaign.tags || []
    });
    setSelectedCampaign(campaign);
    setShowCreateModal(true);
  };

  const handleEditTemplate = (template) => {
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      content: template.content,
      description: template.description || '',
      category: template.category || 'GENERAL'
    });
    setSelectedTemplate(template);
    setShowTemplateModal(true);
  };

  const handleDuplicateCampaign = (campaign) => {
    setCampaignForm({
      name: `${campaign.name} (Copy)`,
      type: campaign.type,
      audience: campaign.audience,
      merchantId: campaign.merchantId || '',
      templateId: campaign.templateId || '',
      subject: campaign.subject,
      content: campaign.content,
      scheduledFor: '',
      tags: campaign.tags || []
    });
    setShowCreateModal(true);
  };

  const handleUseTemplate = (template) => {
    setCampaignForm({
      ...campaignForm,
      templateId: template.id,
      subject: template.subject,
      content: template.content
    });
    setShowTemplateModal(false);
    setShowCreateModal(true);
  };

  const resetCampaignForm = () => {
    setCampaignForm({
      name: '',
      type: 'EMAIL',
      audience: 'ALL_USERS',
      merchantId: '',
      templateId: '',
      subject: '',
      content: '',
      scheduledFor: '',
      tags: []
    });
    setFormErrors({});
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      subject: '',
      content: '',
      description: '',
      category: 'GENERAL'
    });
    setFormErrors({});
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      DRAFT: 'draft',
      SCHEDULED: 'scheduled',
      SENT: 'sent',
      SENDING: 'sending'
    };
    return statusMap[status] || 'draft';
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         campaign.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredTemplates = templates.filter(template => {
    return template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           template.subject.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const stats = {
    total: campaigns.length,
    sent: campaigns.filter(c => c.status === 'SENT').length,
    scheduled: campaigns.filter(c => c.status === 'SCHEDULED').length,
    draft: campaigns.filter(c => c.status === 'DRAFT').length
  };

  return (
    <div className="email-campaigns-container">
      {/* Header */}
      <div className="campaigns-header">
        <h1>Email Campaigns</h1>
        <p>Create, manage, and track your email marketing campaigns</p>
        <div className="campaigns-header-actions">
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} />
            Create Campaign
          </button>
          {activeTab === 'templates' && (
            <button className="btn btn-secondary" onClick={() => setShowTemplateModal(true)}>
              <Plus size={18} />
              Create Template
            </button>
          )}
        </div>
      </div>

      {/* Alerts */}
      {success && (
        <div className="alert alert-success">
          <Check size={20} />
          {success}
        </div>
      )}
      {error && (
        <div className="alert alert-error">
          <X size={20} />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="campaigns-tabs">
        <button
          className={`campaigns-tab ${activeTab === 'campaigns' ? 'active' : ''}`}
          onClick={() => setActiveTab('campaigns')}
        >
          <Mail size={18} />
          Campaigns
        </button>
        <button
          className={`campaigns-tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          <FileText size={18} />
          Templates
        </button>
        <button
          className={`campaigns-tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <BarChart3 size={18} />
          Analytics
        </button>
      </div>

      {/* Content */}
      <div className="campaigns-content">
        {activeTab === 'campaigns' && (
          <>
            {/* Stats */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-card-icon" style={{ background: '#dbeafe' }}>
                  <Mail size={20} color="#1e40af" />
                </div>
                <div className="stat-card-value">{stats.total}</div>
                <div className="stat-card-label">Total Campaigns</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-icon" style={{ background: '#d1fae5' }}>
                  <Check size={20} color="#065f46" />
                </div>
                <div className="stat-card-value">{stats.sent}</div>
                <div className="stat-card-label">Sent</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-icon" style={{ background: '#fef3c7' }}>
                  <Clock size={20} color="#92400e" />
                </div>
                <div className="stat-card-value">{stats.scheduled}</div>
                <div className="stat-card-label">Scheduled</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-icon" style={{ background: '#f3f4f6' }}>
                  <Send size={20} color="#6b7280" />
                </div>
                <div className="stat-card-value">{stats.draft}</div>
                <div className="stat-card-label">Drafts</div>
              </div>
            </div>

            {/* Filters */}
            <div className="campaigns-filters">
              <div className="campaigns-search">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button
                className={`campaigns-filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                onClick={() => setStatusFilter('all')}
              >
                All
              </button>
              <button
                className={`campaigns-filter-btn ${statusFilter === 'SENT' ? 'active' : ''}`}
                onClick={() => setStatusFilter('SENT')}
              >
                Sent
              </button>
              <button
                className={`campaigns-filter-btn ${statusFilter === 'SCHEDULED' ? 'active' : ''}`}
                onClick={() => setStatusFilter('SCHEDULED')}
              >
                Scheduled
              </button>
              <button
                className={`campaigns-filter-btn ${statusFilter === 'DRAFT' ? 'active' : ''}`}
                onClick={() => setStatusFilter('DRAFT')}
              >
                Drafts
              </button>
            </div>

            {/* Campaign List */}
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <div className="empty-state">
                <Mail size={64} />
                <h3>No campaigns found</h3>
                <p>Create your first email campaign to get started</p>
                <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                  <Plus size={18} />
                  Create Campaign
                </button>
              </div>
            ) : (
              <div className="campaigns-grid">
                {filteredCampaigns.map(campaign => (
                  <div key={campaign.id} className="campaign-card">
                    <div className="campaign-card-header">
                      <div>
                        <h3 className="campaign-card-title">{campaign.name}</h3>
                        <div className="campaign-card-meta">
                          <Mail size={14} />
                          <span>{campaign.type}</span>
                          <span>•</span>
                          <span>{new Date(campaign.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <span className={`campaign-status-badge ${getStatusBadgeClass(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </div>

                    <div className="campaign-card-stats">
                      <div className="campaign-stat">
                        <span className="campaign-stat-value">{campaign.recipientCount || 0}</span>
                        <span className="campaign-stat-label">Recipients</span>
                      </div>
                      <div className="campaign-stat">
                        <span className="campaign-stat-value">{campaign.openRate || 0}%</span>
                        <span className="campaign-stat-label">Open Rate</span>
                      </div>
                      <div className="campaign-stat">
                        <span className="campaign-stat-value">{campaign.clickRate || 0}%</span>
                        <span className="campaign-stat-label">Click Rate</span>
                      </div>
                    </div>

                    <div className="campaign-card-actions">
                      <button
                        className="campaign-card-btn"
                        onClick={() => handleViewCampaign(campaign)}
                      >
                        <Eye size={16} />
                        View
                      </button>
                      <button
                        className="campaign-card-btn"
                        onClick={() => handleEditCampaign(campaign)}
                      >
                        <Edit size={16} />
                        Edit
                      </button>
                      <button
                        className="campaign-card-btn"
                        onClick={() => handleDuplicateCampaign(campaign)}
                      >
                        <Copy size={16} />
                        Duplicate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'templates' && (
          <>
            {/* Search */}
            <div className="campaigns-filters">
              <div className="campaigns-search">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Templates List */}
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="empty-state">
                <FileText size={64} />
                <h3>No templates found</h3>
                <p>Create your first email template to reuse content</p>
                <button className="btn btn-primary" onClick={() => setShowTemplateModal(true)}>
                  <Plus size={18} />
                  Create Template
                </button>
              </div>
            ) : (
              <div className="campaigns-grid">
                {filteredTemplates.map(template => (
                  <div key={template.id} className="campaign-card">
                    <div className="campaign-card-header">
                      <div>
                        <h3 className="campaign-card-title">{template.name}</h3>
                        <div className="campaign-card-meta">
                          <FileText size={14} />
                          <span>{template.category || 'GENERAL'}</span>
                          <span>•</span>
                          <span>{new Date(template.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="template-preview">
                      <div className="template-subject"><strong>Subject:</strong> {template.subject}</div>
                      <div className="template-content-preview">
                        {template.content.substring(0, 150)}...
                      </div>
                    </div>

                    <div className="campaign-card-actions">
                      <button
                        className="campaign-card-btn"
                        onClick={() => handleUseTemplate(template)}
                      >
                        <Sparkles size={16} />
                        Use Template
                      </button>
                      <button
                        className="campaign-card-btn"
                        onClick={() => handleEditTemplate(template)}
                      >
                        <Edit size={16} />
                        Edit
                      </button>
                      <button
                        className="campaign-card-btn"
                        onClick={() => handleDeleteTemplate(template.id)}
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'analytics' && (
          <div className="empty-state">
            <BarChart3 size={64} />
            <h3>Campaign Analytics</h3>
            <p>Detailed analytics and insights coming soon</p>
          </div>
        )}
      </div>

      {/* Create/Edit Campaign Modal */}
      {showCreateModal && (
        <div className="campaigns-modal-overlay" onClick={() => {
          setShowCreateModal(false);
          setSelectedCampaign(null);
          resetCampaignForm();
        }}>
          <div className="campaigns-modal" onClick={(e) => e.stopPropagation()}>
            <div className="campaigns-modal-header">
              <h2>{selectedCampaign ? 'Edit Campaign' : 'Create New Campaign'}</h2>
              <button className="campaigns-modal-close" onClick={() => {
                setShowCreateModal(false);
                setSelectedCampaign(null);
                resetCampaignForm();
              }}>
                <X size={20} />
              </button>
            </div>

            <div className="campaigns-modal-body">
              <div className="form-group">
                <label className="form-label required">Campaign Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Spring Sale 2024"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                />
                {formErrors.name && <div className="form-error">{formErrors.name}</div>}
              </div>

              <div className="form-group">
                <label className="form-label required">Campaign Type</label>
                <select
                  className="form-select"
                  value={campaignForm.type}
                  onChange={(e) => setCampaignForm({ ...campaignForm, type: e.target.value })}
                >
                  <option value="EMAIL">Email</option>
                  <option value="IN_APP">In-App Notification</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label required">Target Audience</label>
                <select
                  className="form-select"
                  value={campaignForm.audience}
                  onChange={(e) => setCampaignForm({ ...campaignForm, audience: e.target.value, merchantId: '' })}
                >
                  <option value="ALL_USERS">All Users (Platform-wide)</option>
                  <option value="ALL_MERCHANTS">All Merchants</option>
                  <option value="MERCHANT_CUSTOMERS">Specific Merchant's Customers</option>
                </select>
              </div>

              {campaignForm.audience === 'MERCHANT_CUSTOMERS' && (
                <div className="form-group">
                  <label className="form-label required">Select Merchant</label>
                  <select
                    className="form-select"
                    value={campaignForm.merchantId}
                    onChange={(e) => setCampaignForm({ ...campaignForm, merchantId: e.target.value })}
                  >
                    <option value="">-- Select a merchant --</option>
                    {merchants.map(merchant => (
                      <option key={merchant.id} value={merchant.id}>
                        {merchant.username} ({merchant.email})
                        {merchant.store?.name ? ` - ${merchant.store.name}` : ''}
                      </option>
                    ))}
                  </select>
                  {formErrors.merchantId && <div className="form-error">{formErrors.merchantId}</div>}
                  <div className="form-hint">
                    Select which merchant's customers should receive this campaign
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Use Template (Optional)</label>
                <select
                  className="form-select"
                  value={campaignForm.templateId}
                  onChange={(e) => {
                    const template = templates.find(t => t.id === parseInt(e.target.value));
                    if (template) {
                      setCampaignForm({
                        ...campaignForm,
                        templateId: e.target.value,
                        subject: template.subject,
                        content: template.content
                      });
                    } else {
                      setCampaignForm({ ...campaignForm, templateId: '' });
                    }
                  }}
                >
                  <option value="">-- Start from scratch --</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label required">Subject Line</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Don't miss our spring sale!"
                  value={campaignForm.subject}
                  onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                />
                {formErrors.subject && <div className="form-error">{formErrors.subject}</div>}
              </div>

              <div className="form-group">
                <label className="form-label required">Message Content</label>
                <textarea
                  className="form-textarea"
                  placeholder="Enter your email content here..."
                  value={campaignForm.content}
                  onChange={(e) => setCampaignForm({ ...campaignForm, content: e.target.value })}
                  rows={8}
                />
                {formErrors.content && <div className="form-error">{formErrors.content}</div>}
                <div className="form-hint">
                  Tip: Use variables like {'{{'}customerName{'}}'}  for personalization
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Schedule Send (Optional)</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={campaignForm.scheduledFor}
                  onChange={(e) => setCampaignForm({ ...campaignForm, scheduledFor: e.target.value })}
                />
                <div className="form-hint">
                  Leave empty to send immediately
                </div>
              </div>
            </div>

            <div className="campaigns-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setSelectedCampaign(null);
                  resetCampaignForm();
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={selectedCampaign ? handleUpdateCampaign : handleCreateCampaign}
                disabled={loading}
              >
                {loading ? 'Saving...' : (campaignForm.scheduledFor ? 'Schedule Campaign' : 'Send Now')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Template Modal */}
      {showTemplateModal && (
        <div className="campaigns-modal-overlay" onClick={() => {
          setShowTemplateModal(false);
          setSelectedTemplate(null);
          resetTemplateForm();
        }}>
          <div className="campaigns-modal" onClick={(e) => e.stopPropagation()}>
            <div className="campaigns-modal-header">
              <h2>{selectedTemplate ? 'Edit Template' : 'Create New Template'}</h2>
              <button className="campaigns-modal-close" onClick={() => {
                setShowTemplateModal(false);
                setSelectedTemplate(null);
                resetTemplateForm();
              }}>
                <X size={20} />
              </button>
            </div>

            <div className="campaigns-modal-body">
              <div className="form-group">
                <label className="form-label required">Template Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Welcome Email"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                />
                {formErrors.name && <div className="form-error">{formErrors.name}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Brief description of this template"
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  value={templateForm.category}
                  onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                >
                  <option value="GENERAL">General</option>
                  <option value="WELCOME">Welcome</option>
                  <option value="PROMOTIONAL">Promotional</option>
                  <option value="TRANSACTIONAL">Transactional</option>
                  <option value="NOTIFICATION">Notification</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label required">Subject Line</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Welcome to our platform!"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                />
                {formErrors.subject && <div className="form-error">{formErrors.subject}</div>}
              </div>

              <div className="form-group">
                <label className="form-label required">Template Content</label>
                <textarea
                  className="form-textarea"
                  placeholder="Enter your template content here..."
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                  rows={10}
                />
                {formErrors.content && <div className="form-error">{formErrors.content}</div>}
                <div className="form-hint">
                  Use variables like {'{{'}username{'}}'}, {'{{'}email{'}}'} for dynamic content
                </div>
              </div>
            </div>

            <div className="campaigns-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowTemplateModal(false);
                  setSelectedTemplate(null);
                  resetTemplateForm();
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateTemplate}
                disabled={loading}
              >
                {loading ? 'Saving...' : (selectedTemplate ? 'Update Template' : 'Create Template')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Campaign Modal */}
      {showViewModal && selectedCampaign && (
        <div className="campaigns-modal-overlay" onClick={() => {
          setShowViewModal(false);
          setSelectedCampaign(null);
        }}>
          <div className="campaigns-modal" onClick={(e) => e.stopPropagation()}>
            <div className="campaigns-modal-header">
              <h2>{selectedCampaign.name}</h2>
              <button className="campaigns-modal-close" onClick={() => {
                setShowViewModal(false);
                setSelectedCampaign(null);
              }}>
                <X size={20} />
              </button>
            </div>

            <div className="campaigns-modal-body">
              <div className="campaign-view-section">
                <h3>Campaign Details</h3>
                <div className="campaign-view-grid">
                  <div className="campaign-view-item">
                    <label>Status:</label>
                    <span className={`campaign-status-badge ${getStatusBadgeClass(selectedCampaign.status)}`}>
                      {selectedCampaign.status}
                    </span>
                  </div>
                  <div className="campaign-view-item">
                    <label>Type:</label>
                    <span>{selectedCampaign.type}</span>
                  </div>
                  <div className="campaign-view-item">
                    <label>Audience:</label>
                    <span>{selectedCampaign.audience.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="campaign-view-item">
                    <label>Recipients:</label>
                    <span>{selectedCampaign.recipientCount || 0}</span>
                  </div>
                  <div className="campaign-view-item">
                    <label>Created:</label>
                    <span>{new Date(selectedCampaign.createdAt).toLocaleString()}</span>
                  </div>
                  {selectedCampaign.sentAt && (
                    <div className="campaign-view-item">
                      <label>Sent:</label>
                      <span>{new Date(selectedCampaign.sentAt).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="campaign-view-section">
                <h3>Email Content</h3>
                <div className="campaign-view-item">
                  <label>Subject:</label>
                  <div className="campaign-view-content">{selectedCampaign.subject}</div>
                </div>
                <div className="campaign-view-item">
                  <label>Message:</label>
                  <div className="campaign-view-content campaign-message-content">
                    {selectedCampaign.content}
                  </div>
                </div>
              </div>

              <div className="campaign-view-section">
                <h3>Performance</h3>
                <div className="campaign-view-stats">
                  <div className="stat-card">
                    <div className="stat-card-value">{selectedCampaign.openRate || 0}%</div>
                    <div className="stat-card-label">Open Rate</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-value">{selectedCampaign.clickRate || 0}%</div>
                    <div className="stat-card-label">Click Rate</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="campaigns-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedCampaign(null);
                }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowViewModal(false);
                  handleDuplicateCampaign(selectedCampaign);
                }}
              >
                <Copy size={16} />
                Duplicate Campaign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCampaigns;
